import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { ScreenBlock, CTABand, SectionHead } from "@/components/site/ui";
import { ManagerDashboard } from "@/components/site/mockups";

const GROUPS = [
  {
    eyebrow: "Care Delivery",
    title: "Person-centred care, planned around the individual",
    desc: "Bring together everything the team needs to deliver great care — from a person's overview to their plans, notes and goals.",
    points: ["Person-centred care plans", "About Me / person overview", "Needs, outcomes & future goals", "Daily notes & observations", "Alerts, risks, consent & documents"],
    screenKey: "careplans",
  },
  {
    eyebrow: "Medication",
    title: "Medication management and eMAR",
    desc: "Record and monitor medication with clear MAR / eMAR workflows, including PRN and medication error monitoring.",
    points: ["Medication records", "MAR / eMAR workflows", "PRN medication", "Administration records", "Medication error monitoring"],
    screenKey: "emar",
    reverse: true,
  },
  {
    eyebrow: "Workforce",
    title: "Rota, attendance and staff development",
    desc: "Plan the right people at the right time, and keep training, supervisions and staff records in one place.",
    points: ["Staff rota & visit management", "Attendance", "Leave & holidays", "Training compliance", "Supervisions & appraisals"],
    screenKey: "rota",
  },
  {
    eyebrow: "Payroll & Staff Hours",
    title: "From worked hours to payroll-ready information",
    desc: "AYOBELL Payroll & Staff Hours prepares approved staff hours and gross-pay information for payroll processing. It is not an HMRC payroll engine.",
    points: ["Scheduled, worked & payable hours", "Manager attendance confirmation", "Payroll approval & overtime", "Payroll statements (gross pay)", "CSV / XLSX exports & audit trail"],
    screenKey: "payroll",
    reverse: true,
  },
  {
    eyebrow: "Governance & Quality",
    title: "Evidence and oversight in one place",
    desc: "Keep incidents, safeguarding, complaints, audits and readiness indicators together to support quality and inspection preparation.",
    points: ["Incidents & safeguarding", "Complaints & compliments", "Audits & action plans", "AYOBELL CQC readiness dashboard", "Governance reporting"],
    screenKey: "incidents",
  },
  {
    eyebrow: "Operations",
    title: "Multi-site operations at a glance",
    desc: "Give managers the operational picture across sites, with dashboards, calendars and notifications.",
    points: ["Multi-site management", "Manager dashboards", "Appointments calendar", "Site / house filtering", "Notifications"],
    screenKey: "dashboard",
    reverse: true,
  },
];

export default function Platform() {
  useSEO(
    "The AYOBELL Platform — Care, Medication, Workforce, Payroll & Governance",
    "Explore the AYOBELL care management platform: care delivery, medication (eMAR), workforce & rota, payroll & staff hours, governance & quality, and multi-site operations."
  );
  return (
    <>
      <PageHero
        eyebrow="The Platform"
        title="Everything your care service needs. One connected platform."
        subtitle="Care delivery, medication, workforce, payroll, governance and operations — organised into the workflows your teams already use."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/data-migration", label: "See Data Migration" }}
        screenKey="dashboard"
        screenComp={ManagerDashboard}
      />

      <section className="bg-white pt-20 sm:pt-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead center eyebrow="Overview" title="Six connected areas, one secure platform" desc="Explore each area of AYOBELL with the workflows care teams rely on every day." />
        </div>
      </section>

      {GROUPS.map((g, i) => (
        <section key={g.eyebrow} className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50"} py-16 sm:py-20 px-4 sm:px-6 lg:px-8`}>
          <div className="max-w-7xl mx-auto">
            <ScreenBlock {...g} url={g.screenKey === "cqc" ? "app.ayobell.com/governance" : undefined} />
          </div>
        </section>
      ))}

      <CTABand title="See the whole platform in a personalised demo." />
    </>
  );
}
